// Web writes go through the same push endpoint as the apps (api.md §Web reads): one write path, one
// set of merge rules. The browser keeps its own HLC in localStorage and advances it past every clock
// it sees, so its edits always sort after what's on screen even if the computer's clock is behind.
import type { Change, PushResponse, RowSnapshot } from '@pf/api-types';
import { decodeHlc, encodeHlc, type Hlc, receive, tick } from '@pf/sync-rules';
import { api } from './api.ts';
import { realNow } from './clock.ts';

const NODE_KEY = 'pf.hlc.node';
const CLOCK_KEY = 'pf.hlc.clock';

function node(): string {
  let id = localStorage.getItem(NODE_KEY);
  if (!id) {
    id = Array.from(crypto.getRandomValues(new Uint8Array(4)), (b) => b.toString(16).padStart(2, '0')).join(
      '',
    );
    localStorage.setItem(NODE_KEY, id);
  }
  return id;
}

function load(): Hlc {
  const raw = localStorage.getItem(CLOCK_KEY);
  return raw ? decodeHlc(raw) : { ms: 0, counter: 0, node: node() };
}

/** Advance past every clock in rows we've just loaded. */
export function observe(rows: RowSnapshot[]): void {
  let clock = load();
  for (const row of rows)
    for (const h of Object.values(row.hlc)) clock = receive(clock, decodeHlc(h), realNow());
  localStorage.setItem(CLOCK_KEY, encodeHlc(clock));
}

export type Edit = Omit<Change, 'hlc' | 'ownerUserId'> & { ownerUserId?: string | null };

/** Push edits; resolves with what the server refused, its rows for those, and the clock the edits carry. */
export async function write(edits: Edit[]): Promise<PushResponse & { hlc: string }> {
  const clock = tick(load(), realNow());
  localStorage.setItem(CLOCK_KEY, encodeHlc(clock));
  const hlc = encodeHlc(clock);
  const res = await api<PushResponse>('/sync/push', {
    method: 'POST',
    body: { clientId: node(), changes: edits.map((e) => ({ ownerUserId: null, ...e, hlc })) },
  });
  return { ...res, hlc };
}
